import type {
  BoardDefinition,
  GameState,
  Point,
  VisibleBead,
} from "./types";

const keyOf = ({ x, y }: Point): string => `${x},${y}`;

export function getVisibleBeads(
  board: BoardDefinition,
  state: Pick<GameState, "removedBeadIds">,
): readonly VisibleBead[] {
  const removed = new Set(state.removedBeadIds);
  const visible: VisibleBead[] = [];

  for (const chunk of board.chunks) {
    for (const cell of chunk.cells) {
      for (let layer = cell.layers.length - 1; layer >= 0; layer -= 1) {
        const bead = cell.layers[layer];
        if (!bead) {
          continue;
        }
        if (!removed.has(bead.id)) {
          visible.push({
            beadId: bead.id,
            color: bead.color,
            chunkId: chunk.id,
            layer,
            x: chunk.origin.x + cell.x,
            y: chunk.origin.y + cell.y,
          });
          break;
        }
      }
    }
  }

  return visible.sort(
    (left, right) =>
      left.y - right.y || left.x - right.x || left.beadId.localeCompare(right.beadId),
  );
}

export function getConnectedBlock(
  board: BoardDefinition,
  state: Pick<GameState, "removedBeadIds">,
  start: Point,
): readonly VisibleBead[] {
  const byPosition = new Map(
    getVisibleBeads(board, state).map((bead) => [keyOf(bead), bead]),
  );
  const first = byPosition.get(keyOf(start));
  if (!first) {
    return [];
  }

  const queue: VisibleBead[] = [first];
  const visited = new Set<string>([keyOf(first)]);
  const block: VisibleBead[] = [];
  const directions = [
    { x: 0, y: -1 },
    { x: -1, y: 0 },
    { x: 1, y: 0 },
    { x: 0, y: 1 },
  ] as const;

  for (let index = 0; index < queue.length; index += 1) {
    const bead = queue[index];
    if (!bead) {
      continue;
    }
    block.push(bead);
    for (const direction of directions) {
      const neighborKey = keyOf({
        x: bead.x + direction.x,
        y: bead.y + direction.y,
      });
      const neighbor = byPosition.get(neighborKey);
      if (neighbor?.color === first.color && !visited.has(neighborKey)) {
        visited.add(neighborKey);
        queue.push(neighbor);
      }
    }
  }

  return block;
}
